import { unsafeCSS, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import ResizeObserver from 'resize-observer-polyfill';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import type { ManagerAndTeamMembers } from './manager-team.model.js';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import { Constants } from './manager-team.constants';
import { IconSVG } from '../../common/utils/icon.js';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML.js';
import style from './manager-team.scss?inline';
import { ManagerTeamFragments } from '../../common/fragments';

/**
 * @tag pop-manager-and-team-component
 * @property {ManagerAndTeamMembers} [componentObject] Manager and team content
 * @attribute {ManagerAndTeamMembers} [data-component-object] Manager and team content
 */
@customElement(Constants.TAG_NAME)
export class PopManagerAndTeamComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  static readonly nestedComponents = Constants.NESTED_COMPONENTS;

  /**
   * @internal
   */
  componentName = PopManagerAndTeamComponent.tagName;

  /**
   * @internal
   */
  fragment = ManagerTeamFragments.managerTeamFragment;

  /**
   * @internal
   */
  @state()
  private breakpointIsXLUp = false;

  /**
   * @internal
   */
  private resizeObserver!: ResizeObserver;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: ManagerAndTeamMembers;

  constructor() {
    super(new ApiService());
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(
      this.updateBreakpointStatus.bind(this),
    );
    this.resizeObserver?.observe(document.body);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
  }

  updateBreakpointStatus() {
    const computedStyle = getComputedStyle(this);
    const xLargeBreakpoint = computedStyle.getPropertyValue(
      Constants.BEACON_SYSTEM_BREAKPOINT_X_LARGE,
    );

    this.breakpointIsXLUp = window.matchMedia(
      `(min-width: ${xLargeBreakpoint})`,
    ).matches;
  }

  renderManagerAndTeamTitle() {
    return html`
      <h3
        class="manager-and-team-title b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.MANAGER_AND_TEAM}
      </h3>
    `;
  }

  protected processResponse(_response): void {
    this.componentObject = _response?.teamExhibit;
  }

  getStockAvatarIcon() {
    return IconSVG.stockAvatarDataURL();
  }

  renderTeamMemberImage(imageSource: string) {
    const stockAvatarDataURL = IconSVG.stockAvatarDataURL();
    const quotedStockAvatarDataURL = `"${stockAvatarDataURL}"`;

    return html`
      <div class="beacon-contact-tile__icon">
        <img
          src="${imageSource || stockAvatarDataURL}"
          alt=""
          width="128"
          height="128"
          loading="lazy"
          onerror="
            this.onerror = null;
            this.src = ${quotedStockAvatarDataURL};
          "
        />
      </div>
    `;
  }

  renderTeamMemberName(name: string, bioKey: string) {
    if (name && bioKey) {
      return html`
        <h4 class="beacon-contact-tile__title">
          <a href="${this.getBioLink(bioKey)}" target="_blank">${name}</a>
        </h4>
      `;
    }

    return html``;
  }

  renderTeamMemberJobTitle(jobTitle: string) {
    if (jobTitle) {
      return html` <p class="beacon-contact-tile__job-title">${jobTitle}</p> `;
    }

    return html``;
  }

  renderTeamMemberDescription(
    yearsOfExperience: number,
    yearsAtCompany: number,
  ) {
    if (yearsOfExperience && yearsAtCompany) {
      return html`
        <div class="beacon-contact-tile__description b-flow-space">
          <p>${yearsAtCompany} ${Constants.YEAR_AT_TROWE_PRICE}</p>
          <p>${yearsOfExperience} ${Constants.YEARS_OF_EXPERIENCE}</p>
        </div>
      `;
    }

    return html``;
  }

  // Using 16-14-14-14 for buttons font size for now
  getLinkScale() {
    return this.breakpointIsXLUp ? Constants.NORMAL : Constants.MINI;
  }

  getBioLink(bioKey: string) {
    return `/financial-intermediary/${this.country}/${this.language}/search.html?q=${bioKey}`;
  }

  renderBioLink(bioKey: string) {
    if (bioKey) {
      return html`
        <beacon-button
          variant="tertiary"
          scale="${this.getLinkScale()}"
          link-href="${this.getBioLink(bioKey)}"
          link-target="_blank"
          data-test-id="bio-link"
        >
          ${Constants.VIEW_BIO}
        </beacon-button>
      `;
    }

    return html``;
  }

  private renderDisclosures() {
    const disclaimers = this.componentObject?.disclaimers;

    if (!disclaimers || disclaimers.length === 0) {
      return html``;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    if (
      this.componentObject &&
      this.componentObject.employeeBios &&
      this.componentObject.employeeBios.length > 0
    ) {
      const sortedTeamMembers = this.componentObject.employeeBios
        .filter((teamMember) => teamMember.teamRank)
        .sort((a, b) => a.teamRank - b.teamRank);

      return this.renderComponent(html`
        <section class="b-container">
          ${this.renderManagerAndTeamTitle()}
          <div class="b-grid">
            ${sortedTeamMembers.map(
              (teamMember) => html`
                <div class="b-grid-item--12-col b-md:6-col">
                  <beacon-contact-tile
                    class="beacon-contact-tile__vertical-fill"
                  >
                    <div slot="body">
                      ${this.renderTeamMemberImage(teamMember.imageSource)}
                      ${this.renderTeamMemberName(
                        teamMember.name,
                        teamMember.bioKey,
                      )}
                      ${this.renderTeamMemberJobTitle(teamMember.jobTitle)}
                      ${this.renderTeamMemberDescription(
                        teamMember.yearsOfExperience,
                        teamMember.yearsAtCompany,
                      )}
                    </div>
                    <div slot="footer">
                      <div
                        class="links-container b-margin-block-start--micro--normal"
                      >
                        ${this.renderBioLink(teamMember.bioKey)}
                      </div>
                    </div>
                  </beacon-contact-tile>
                </div>
              `,
            )}
          </div>
          ${this.renderDisclosures()}
        </section>
      `);
    }

    return this.renderComponent(html`
      <section class="b-container">
        ${this.renderManagerAndTeamTitle()}
        <pop-no-data-available-component
          .message="${Constants.MANAGER_AND_TEAM_UNAVAILABLE}"
        ></pop-no-data-available-component>
      </section>
    `);
  }
}
